/**
 * 前端监控统一出口(阿里云 ARMS 用户体验监控 / @arms/rum-browser)。
 * UNBO-285:与 gc-sku / gc-pay / gc-access-new 同一套门面,除本文件外仅 index.jsx 增加一行 import,
 * 不改任何业务代码。自动采集即可获得:
 * PV(按路由模式聚合)/API 成功率(HTTP 状态口径)/JS 错误/白屏检测/WebVitals/会话,
 * 全部事件自动携带鸿蒙、微信维度。
 *
 * 设计要点:
 * 1. SDK 动态 import 进独立 chunk(arms-rum),静态引入会让首屏 +60KB 左右 gzip;
 *    RUM_ENDPOINT 为空时监控整体停用(连 chunk 都不下载),对业务零影响——这是唯一开关。
 * 2. 所有调用均 try/catch 兜底,监控自身异常绝不影响业务。
 * 3. Monitor.* 门面与其他三仓一致,后续埋自定义事件直接调用。
 *
 * 本仓与其他三仓的差异(照抄会静默失效,勿改回):
 * - 路由是 react-router v6(6.28.1),页面名必须用 matchRoutes;
 *   v5 那套 matchPath(pathname, {path, exact}) 在 v6 下直接抛 TypeError,
 *   会被 catch 吞掉、退化成原始 pathname,PV 被 :pageType 之类的参数劈散。
 * - #root 里预置了加载骨架(见 public/index.html),HAS_CONTENT 恒为真,
 *   白屏检测必须换成"还停在骨架 = 白屏"的自定义 tester,否则死 Loading 永远报不出来。
 * - 旧版 bl.js(__bl)已随本次迁移下线,其存量调用 __bl.sum(长按埋点)已改走
 *   Monitor.count(见 commonHelper.js 的 longTimePressUpload),事件名保持不变便于对账。
 *
 * ⚠ endpoint 未填之前本仓处于「无监控」状态:旧 bl.js 已摘、RUM 未启用,
 *   长按埋点等自定义事件会被直接丢弃(不报错)。填入 endpoint 后自动恢复。
 */
/* global BUILD_VERSION */
import { matchRoutes } from 'react-router-dom';
// 仅读取路由表做页面名聚合(路由不反向依赖本文件,无循环)
import routerConfig from '@/router/index';

// TODO(UNBO-285): 待 ARMS 控制台为 gc-p 创建 RUM 应用后填入 endpoint(含 workspace 与 service_id)。
// 其他三仓各自独立 service_id,此处不可复用它们的地址,否则数据会串到别的应用。
// 置空 = 监控整体停用(chunk 都不下载),对线上零影响,可安全先合。
const RUM_ENDPOINT = 'https://proj-xtrace-74efae865c42939d95689731fd0b031-cn-hangzhou.cn-hangzhou.log.aliyuncs.com/rum/web/v2?workspace=default-cms-1952650058542185-cn-hangzhou&service_id=j85n1wxbhl@019935af93856d99f6841';

let rum = null;
// SDK 就绪前缓冲:[method, args],就绪后按序补发
const rumQueue = [];

// ===== 页面名口径:URL 路径 -> 路由模式(如 /bds-h5/bdskf-bds01/:pageType),聚合不被参数劈散 =====
const { routes } = routerConfig;
const pageNameCache = new Map();

function getPageNameFromRoutes(pathname) {
  if (pageNameCache.has(pathname)) {
    return pageNameCache.get(pathname);
  }
  let pageName = pathname;
  try {
    // v6:返回从父到子的整条匹配分支,子路由 path 是相对的,需拼接
    const branch = matchRoutes(routes, pathname);
    const segs = (branch || []).map((m) => m.route.path || '').filter(Boolean);
    if (segs.length) {
      // 末段保留 /*(说明只命中了通配父级,归到该父级的通配桶),父段的 /* 去掉再拼
      pageName = segs
        .map((p, i) => (i === segs.length - 1 ? p : p.replace(/\/\*$/, '')))
        .join('/')
        .replace(/\/{2,}/g, '/');
      if (!pageName.startsWith('/')) {
        pageName = `/${pageName}`;
      }
    } else {
      // 未匹配 = 坏链接/404 面,计数度量
      rumSend('sendCustom', { type: 'count', name: 'route_unmatched', group: 'route', value: 1 });
    }
  } catch (e) {
    // 解析失败回退原始路径
  }
  pageNameCache.set(pathname, pageName);
  return pageName;
}

// ===== UA 维度(自包含检测,不依赖工具库)=====
// 值必须是 string/number:SDK 组包时对全局 properties 走 verifyProperties,
// 会删掉一切非 string/number 的值;三个键全传布尔会被删光,properties 整体变 undefined,
// 维度静默丢失(不报错、控制台只是永远空着)。故用 1/0 而非 true/false。
function uaProperties() {
  try {
    const ua = navigator.userAgent || '';
    return {
      harmony: /OpenHarmony|ArkWeb/i.test(ua) ? 1 : 0,
      wechat: /MicroMessenger/i.test(ua) ? 1 : 0,
      miniprogram: /miniProgram/i.test(ua) ? 1 : 0,
    };
  } catch (e) {
    return {};
  }
}

// ===== 白屏判据:#root 预置了加载骨架,"还停在骨架"才是白屏 =====
// React 首次 render 会替换 #root 全部子节点,骨架随之消失;
// 3s 后骨架还在 = 主包没跑起来/路由 chunk 没加载出来 = 用户看到的死 Loading。
function stuckOnSkeleton(node) {
  try {
    const stillSkeleton = !!node.querySelector('.page-loading-warp');
    return {
      hasContent: !stillSkeleton,
      message: stillSkeleton ? 'stuck_on_loading_skeleton' : '',
    };
  } catch (e) {
    // 判据本身出错时不误报白屏
    return { hasContent: true };
  }
}

function initRum() {
  if (!RUM_ENDPOINT || typeof window === 'undefined') {
    return;
  }
  import(/* webpackChunkName: "arms-rum" */ '@arms/rum-browser').then((mod) => {
    const armsRum = mod.default;
    armsRum.init({
      endpoint: RUM_ENDPOINT,
      env: (typeof ENV !== 'undefined' && ENV === 'prod') ? 'prod' : 'daily',
      // 构建版本维度(webpack 未注入 BUILD_VERSION 时自动省略)
      version: typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : undefined,
      // history 路由自动 PV
      spaMode: 'history',
      // 全局维度:所有事件可按鸿蒙/微信切片
      properties: uaProperties(),
      parseViewName: (url) => {
        try {
          const { pathname } = new URL(url);
          return getPageNameFromRoutes(pathname);
        } catch (e) {
          return url;
        }
      },
      // 不配 evaluateApi:API 成败按 SDK 默认 HTTP 状态口径(业务失败走 200+code!=0 时不计失败);
      // 网关生成的 16-hex trace_id 由 SDK 从响应头自动采集,前端无需注入/透传。
      // tracing 不开:SDK 自带 tracing 会另注入 32-hex traceparent 与网关 traceId 撞车、断对账。
      // 埋点/统计类接口不进 API 面板,避免稀释成功率
      filters: {
        resource: [/customer_track/, /wx_track/, /cnzz\.com/, /z_stat\.php/],
      },
      // 白屏检测:判据见 stuckOnSkeleton。
      // 形态说明:采集器消费 whiteScreen.detectionRules + test_when 数组(源码坐实,d.ts 的 when 单值与实现不符)
      whiteScreen: {
        detectionRules: [
          { target: '#root', test_when: ['LOAD', 'ROUTE_CHANGE'], delay: 3000, tester: stuckOnSkeleton },
          { target: '#root', test_when: ['ERROR'], delay: 1000, tester: stuckOnSkeleton },
        ],
      },
      // 动态配置:不发版即可在控制台调采样/开关(launch-first 本地先行,不阻塞启动)
      remoteConfig: {
        enable: true,
      },
    }).then(() => {
      // 必须等 init 完成再置就绪:init 第一件事就是 await configManager.init,
      // 在那之前 client 还没装 collector/processor/reporter,此时发事件会被静默丢弃。
      rum = armsRum;
      rumQueue.splice(0, rumQueue.length).forEach((item) => {
        try {
          rum[item[0]](...item[1]);
        } catch (e) {
          // 监控自身异常绝不能影响业务
        }
      });
    });
  }).catch(() => {
    // chunk 加载失败(弱网/被拦):本会话放弃监控,不影响业务
    rumQueue.length = 0;
  });
}
initRum();

/** 上报:SDK 未就绪时入队(未启用则直接丢弃),异常静默 */
function rumSend(method, ...args) {
  if (rum) {
    try {
      rum[method](...args);
    } catch (e) {
      // 监控自身异常绝不能影响业务
    }
    return;
  }
  if (RUM_ENDPOINT) {
    rumQueue.push([method, args]);
  }
}

function toError(err) {
  if (err instanceof Error) {
    return err;
  }
  if (typeof err === 'string') {
    return new Error(err);
  }
  try {
    return new Error(JSON.stringify(err));
  } catch (e) {
    return new Error('unknown_error');
  }
}

function toProperties(ctx) {
  if (!ctx) {
    return undefined;
  }
  if (typeof ctx === 'object') {
    return ctx;
  }
  return { ctx: String(ctx) };
}

/**
 * 事件名 -> 业务域(group),自动上报,控制台按 group 聚合链路。
 * 首段查表(stage_/stuck_ 取第二段),表外首段本身即域。
 */
const GROUP_MAP = {
  boot: 'boot',
  init: 'init',
  auth: 'auth',
  token: 'auth',
  openid: 'auth',
  chunk: 'chunk',
  suspense: 'chunk',
  route: 'route',
  transition: 'route',
  return: 'return',
  pay: 'pay',
  sign: 'sign',
  refund: 'refund',
  invite: 'invite',
  sku: 'sku',
  order: 'order',
  orders: 'order',
  register: 'register',
  survey: 'survey',
  identify: 'user',
};

function groupOf(name) {
  const segs = String(name || '').split('_');
  let head = segs[0];
  if ((head === 'stage' || head === 'stuck') && segs[1]) {
    head = segs[1];
  }
  return GROUP_MAP[head] || head || 'app';
}

const Monitor = {
  /** 计数 / 漏斗事件 */
  count(key) {
    rumSend('sendCustom', { type: 'count', name: key, group: groupOf(key), value: 1 });
  },

  /** 阶段结局:stage_<stage>_<outcome>,用于流程漏斗统计 */
  stage(stage, outcome) {
    const key = `stage_${stage}_${outcome}`;
    rumSend('sendCustom', { type: 'count', name: key, group: groupOf(key), value: 1 });
  },

  /** 卡死看门狗:计数 + 带上下文的异常 */
  stuck(phase, ctx) {
    const key = `stuck_${phase}`;
    rumSend('sendCustom', { type: 'count', name: key, group: groupOf(key), value: 1 });
    rumSend('sendException', {
      name: key,
      message: `stuck:${phase}`,
      properties: { group: groupOf(key), ...toProperties(ctx) },
    });
  },

  /** 耗时统计:控制台按 value 均值/分位聚合 */
  avg(key, ms) {
    rumSend('sendCustom', { type: 'timing', name: key, group: groupOf(key), value: ms });
  },

  /** JS 错误上报,ctx 走结构化 properties;ctx.stage 自动推导 group */
  error(err, ctx) {
    const e = toError(err);
    const props = ctx ? { ...toProperties(ctx) } : undefined;
    if (props && props.stage && !props.group) {
      props.group = groupOf(props.stage);
    }
    rumSend('sendException', {
      name: e.name || 'Error',
      message: e.message,
      stack: e.stack,
      properties: props,
    });
  },

  /** 手动 API 上报,补报「没真正发出」的请求;time 必须是 number,否则 SDK 静默丢弃 */
  api(name, success, time, code, msg) {
    rumSend('sendResource', {
      name,
      type: 'api',
      duration: time,
      success: success ? 1 : 0,
      status_code: code,
      message: msg,
    });
  },

  /**
   * 绑定业务身份。注意:SDK 组包时显式跳过 user.id(固定用其匿名 uid_xxx),
   * 业务 uid 走 tags 才能落到上报数据里。
   */
  identify(uid, username) {
    if (!uid) {
      return;
    }
    const user = { tags: String(uid) };
    if (username) {
      user.name = String(username);
    }
    rumSend('setConfig', 'user', user);
    rumSend('sendCustom', { type: 'count', name: 'identify', group: 'user', value: 1 });
  },
};

export default Monitor;
